/**
 *
 * @param {String} text - The text to be displayed.
 * @param {Object} colors - The colors object containing the color classes and their corresponding values.
 * @param {String} notFoundDefault - The default color class to be used.
 * @param {String} textEmptyDefault - The default text to be displayed if the text is empty.
 * @returns {JSX.Element} The span element with the color class.
 *
 * @example
 * const colors = [
 *    {
 *      colorClass: "text-green-500",
 *      forValues: ["Active", "Available"]
 *   },
 *   {
 *     colorClass: "text-red-500",
 *     forValues: ["Inactive", "Unavailable"]
 *   }];
 *
 * const span = getSpanWithColor("Active", colors, notFoundDefault);
 *
 */
export const getSpanWithColor = (
    text,
    colors = defaultStatusColors,
    notFoundDefault = "text-gray-200",
    textEmptyDefault = "--"
) => {
    try {
        if (typeof text === "boolean") {
            text = text ? "Yes" : "No";
        }
        if (!text || text === "" || text === undefined || text === null) {
            return <span className={`span-with-color ${notFoundDefault}`}>{textEmptyDefault}</span>;
        }

        let colorValue = notFoundDefault;
        let found = false;
        for (const color of colors) {
            for (const value of color.forValues) {
                if (value?.toLowerCase() === text?.toLowerCase()) {
                    found = true;
                    colorValue = color.colorClass;
                    break;
                }
            }
            if (found) break;
        }

        return <span className={`span-with-color ${colorValue}`}>{text}</span>;
    } catch (error) {
        console.error("getSpanWithColor", text);
        return <span className={notFoundDefault}>{textEmptyDefault}</span>;
    }
};

/**
 * Returns a span element with the specified color name or class.
 *
 * @param {string} text - The text to be displayed in the span.
 * @param {string} color - The color class to be applied to the span. EX: "green", "green-500", "red", "blue", "orange", "blue-600", "gray", "gray-100", "gray-200", "gray-300"
 * @returns {JSX.Element} The span element with the specified color.
 *
 * @example
 * const span = getSimpleColorSpan("Active", "green");
 */
export const getSimpleColorSpan = (text, color) => {
    let colorClassName = "text-gray-200";

    if (color === "green" || color === "green-500") colorClassName = "text-green-500";
    else if (color === "red" || color === "red-500") colorClassName = "text-red-500";
    else if (color === "blue" || color === "blue-500") colorClassName = "text-blue-500";
    else if (color === "orange" || color === "orange-500") colorClassName = "text-orange-500";
    else if (color === "blue-600") colorClassName = "text-blue-600";
    else if (color === "gray" || color === "gray-100") colorClassName = "text-gray-100";
    else if (color === "gray-200") colorClassName = "text-gray-200";
    else if (color === "gray-300") colorClassName = "text-gray-300";

    const style = {
        display: "block",
        overflow: "hidden",
        width: "100%",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis"
    };
    return (
        <label className={colorClassName} style={style}>
            {text}
        </label>
    );
};

export const getOpenHoursSpan = text => {
    if (text === "" || text === undefined || text === null || typeof text !== "boolean") {
        return <span className="text-gray-100">--</span>;
    }

    let colorValue = "text-orange-500";
    if (text === true) {
        text = "Open";
        colorValue = "text-green-500";
    } else {
        text = "Closed";
    }

    return <span className={colorValue}>{text}</span>;
};

export const defaultStatusColors = [
    {
        colorClass: "text-blue-600",
        colorValue: "var(--text-icons-blue-600)",
        forValues: ["Active", "Fulfilling", "Connected", "Affiliated", "Accepted", "Processed"]
    },
    {
        colorClass: "text-green-500",
        colorValue: "var(--text-icons-green-500)",
        forValues: [
            "Available",
            "Exported to POS",
            "Exported",
            "Invited",
            "Invite-Resent",
            "Completed",
            "Yes",
            "Enrolling",
            "Enrolled",
            "Pre-Enrolled",
            "Activating",
            "Connecting",
            "Integrated",
            "Ongoing",
            "Sent",
            "New",
            "Delivered",
            "Shared",
            "Imported",
            "Processing",
            "Paid",
            "Ready",
            "Synced",
            "Linked"
        ]
    },
    {
        colorClass: "text-orange-500",
        colorValue: "var(--text-icons-orange-500)",
        forValues: [
            "Suspended",
            "Pending",
            "N/A",
            "Request",
            "Requested",
            "Not affiliated",
            "Suspending",
            "Removing",
            "Not fulfilling",
            "Not active",
            "Accepted, not yet sent",
            "Edited by brand",
            "AcceptedByRetailBrand",
            "Edited",
            "EditAccepted",
            "Sent for approval",
            "Missing product images",
            "Missing Images",
            "Archived",
            "Pending rules",
            "Pending master data"
        ]
    },
    {
        colorClass: "text-red-500",
        colorValue: "var(--text-icons-red-500)",
        forValues: [
            "Inactive",
            "Unavailable",
            "Not connected",
            "Failed",
            "Cancelled",
            "Declined",
            "No",
            "Voided",
            "Revoked",
            "Not integrated",
            "DeclinedByRetailBrand",
            "DeclinedByLabelBrand",
            "EditDeclined",
            "Disconnected",
            "POS not connected",
            "Removed",
            "Unpaid"
        ]
    },
    {
        colorClass: "text-gray-300",
        colorValue: "var(--text-icons-gray-300)",
        forValues: ["--"]
    }
];

export const getColorFromStatus = (status, colorMap = defaultStatusColors, notFoundDefault = "text-gray-200") => {
    let color = notFoundDefault;
    //if (status === "Pending") debugger;
    if (colorMap.find(color => color.forValues.includes(status))) {
        color = colorMap.find(color => color.forValues.includes(status))?.colorValue;
    }
    return color;
};

export const accountStatusColors = [
    {
        colorClass: "text-blue-500",
        forValues: ["Active"]
    },
    {
        colorClass: "text-green-500",
        forValues: ["Enrolled", "Pre-Enrolled", "Enrolling", "Activating"]
    }
];

export const getStatusValue = (row, statusFromPath) => {
    const fromPath = statusFromPath ?? "status";
    const status = getArrayFieldValue(row, fromPath.split(".") || "Unknown");
    //console.log("status", status);
    return status;
};

const getArrayFieldValue = (row, array) => {
    if (!Array.isArray(array)) return "Unknown";

    let res = row;
    for (let i = 0; i < array.length; i++) {
        res = res?.[array[i]];
    }
    return res;
};
